import type { ReactNode } from "react";

import {
  premiumTabs,
  settingsTabs,
  tabsWeb,
  tabsWebNP,
} from "../../layouts/Mobile/tabs";
import { routes } from "../../router/mobile/routes";

export interface AppNavigationItem {
  key: string;
  label: string;
  icon: ReactNode;
}

interface NavigationContext {
  groups: readonly string[];
  isNursePractitioner: boolean;
  /** The selected clinic has an EHR (``ehr_id != 0``). Without one, Insights
   *  and Vitals have no data, so MobileLayout hides them. */
  clinicHasEhr: boolean;
  hasDeviceOrdersAccess: boolean;
  /** Compassus users/clinics: Navigator is the only thing they don't get
   *  here (they have the Compassus Navigator instead). */
  isCompassus: boolean;
}

const isAllowedForGroups = (
  requiredGroups: string[] | undefined,
  userGroups: readonly string[]
): boolean =>
  !requiredGroups?.length ||
  requiredGroups.some((group) => userGroups.includes(group));

/**
 * The expanded menu reuses the existing navigation declarations so route names,
 * icons and role gates remain owned by one source instead of drifting between
 * the legacy and 2026 shells.
 */
export const getMoreNavigationItems = ({
  groups,
  isNursePractitioner,
  clinicHasEhr,
  hasDeviceOrdersAccess,
  isCompassus,
}: NavigationContext): AppNavigationItem[] => {
  const standardTabs = isNursePractitioner ? tabsWebNP : tabsWeb;
  // Navigator (the NP list's first tab) is for everyone, NP or not, except
  // Compassus.
  const navigatorTab = tabsWebNP.find(
    (tab) => tab.key === routes.evaluationAndManagement.path
  );
  const candidates = [
    ...(navigatorTab ? [navigatorTab] : []),
    ...standardTabs,
    ...premiumTabs,
    ...settingsTabs,
  ];
  // The same runtime visibility rules MobileLayout applies to its tabs, so the
  // menu never offers a route the user can't use. Intercom needs the native
  // app's bridge, and this shell is web-only.
  const hidden = new Set<string>([routes.intercom.path]);
  if (!clinicHasEhr) {
    hidden.add(routes.Insights.path);
    hidden.add(routes.vitalSync.path);
  }
  if (!hasDeviceOrdersAccess) hidden.add(routes.deviceOrders.path);
  if (isCompassus) hidden.add(routes.evaluationAndManagement.path);

  const seen = new Set<string>();
  return candidates.flatMap((item) => {
    const requiredGroups = "groups" in item ? item.groups : undefined;
    if (
      typeof item.key !== "string" ||
      !item.key.startsWith("/") ||
      item.key === routes.dashboard.path ||
      item.key === routes.overview.path ||
      item.key === routes.iq.path ||
      item.key === routes.settings.path ||
      hidden.has(item.key) ||
      seen.has(item.key) ||
      !isAllowedForGroups(requiredGroups, groups)
    ) {
      return [];
    }
    seen.add(item.key);
    return [{ key: item.key, label: item.title, icon: item.icon }];
  });
};
